// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
// NexoraX Viral Swarm - Automated Social Dispatcher Node (Vanguard Command)
// Intellectual Property Status: Patent-Readiness Protected Assets, © NexoraX Digital Ltd (SC887095)

import React, { useState, useEffect, useRef } from 'react';
import { 
  Share2, Send, Cpu, Tv, Globe, Shield, RefreshCw, Layers, 
  CheckCircle, Flame, Users, Sparkles, Code, Play, Pause, 
  Landmark, Terminal, Radio, HelpCircle, Activity, ExternalLink,
  Target, Zap, Hash, BarChart3, AlertCircle, Sliders
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface SwarmNodeLogs {
  timestamp: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'INJECT';
  message: string;
}

export const NexoraXViralSwarm: React.FC = () => {
  // Configuration Status Parameters
  const [swarmStatus, setSwarmStatus] = useState<'STANDBY' | 'ACTIVE' | 'DISPATCHING'>('STANDBY');
  const [nodesCount, setNodesCount] = useState<number>(10000);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([
    'LINKEDIN', 'FACEBOOK', 'TIKTOK', 'X', 'INSTAGRAM', 'YOUTUBE'
  ]);
  
  // Advanced Optimizers
  const [category91Optimization, setCategory91Optimization] = useState<boolean>(true);
  const [autoUpdateBios, setAutoUpdateBios] = useState<boolean>(true);
  const [pinSettlementLinks, setPinSettlementLinks] = useState<boolean>(true);
  const [injectQrAssetPack, setInjectQrAssetPack] = useState<boolean>(true);
  
  // Custom Campaign Details
  const [campaignHashtags, setCampaignHashtags] = useState<string>("#Nexorax #Category91 #DunfermlineFoundry #AssetInjection");
  const [customBriefingText, setCustomBriefingText] = useState<string>("NexoraX acquisition matrices online. Direct SecurePayment settlement protocol validated across all 10,000 nodes. Sync to Sort: Verified Payment Configuration | Acc: Secure Billing Provider.");

  // Simulation Telemetry
  const [progress, setProgress] = useState<number>(0);
  const [activeInjectedCount, setActiveInjectedCount] = useState<number>(0);
  const [conversionRate, setConversionRate] = useState<number>(0);
  const [estimatedTrafficReach, setEstimatedTrafficReach] = useState<number>(0);
  const [realtimeTelemetryLogs, setRealtimeTelemetryLogs] = useState<SwarmNodeLogs[]>([
    { timestamp: "16:07:22", type: "INFO", message: "NEXORAX COGNITIVE VIRAL NODE-09.2 ONLINE // WAITING FOR INJECT DIRECTIVES" },
    { timestamp: "16:07:23", type: "INFO", message: "VERIFIED PREAMBLE ROUTING LATCHED SECURE" },
    { timestamp: "16:07:24", type: "SUCCESS", message: "AYRSHARE API BRIDGE SYNCHRONIZED ON DISTRIBUTED HETZNER HUB (5.161.42.190)" }
  ]);

  // Visual simulation grid animation frames
  const [gridCells, setGridCells] = useState<('idle' | 'injected' | 'active')[]>(
    Array(120).fill('idle')
  );

  const logsEndRef = useRef<HTMLDivElement>(null);
  const dispatchIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [realtimeTelemetryLogs]);

  // Platforms toggle helper
  const handleTogglePlatform = (platform: string) => {
    if (swarmStatus === 'DISPATCHING') return;
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  // Run swarm automated social dispatch simulation
  const initiateViralDispatcher = () => {
    if (swarmStatus === 'DISPATCHING') return;
    if (selectedPlatforms.length === 0) {
      setRealtimeTelemetryLogs(prev => [
        ...prev, 
        { timestamp: new Date().toLocaleTimeString(), type: "WARNING", message: "ABORT: NO ACTIVE PLATFORMS NOMINATED FOR DISPATCH PIPELINE." }
      ]);
      return;
    }

    setSwarmStatus('DISPATCHING');
    setProgress(0);
    setActiveInjectedCount(0);
    setConversionRate(0);
    setEstimatedTrafficReach(0);

    // Initial log sequence
    setRealtimeTelemetryLogs([
      { timestamp: new Date().toLocaleTimeString(), type: "INFO", message: `[SWARM] INITIALIZING MASSIVE MULTI-CHANNEL DEPLOYMENT: Target Count ${nodesCount} Nodes.` },
      { timestamp: new Date().toLocaleTimeString(), type: "INFO", message: `[SWARM] NOMINATED BROADCASTS: [${selectedPlatforms.join(', ')}]` },
      { timestamp: new Date().toLocaleTimeString(), type: "INJECT", message: `[INJECT] Bundling NexoraX QR Asset Packs targeting print-ready scaling.` },
    ]);

    // Fast-paced interval simulation representing 0.8s benchmark velocities
    let currentStep = 0;
    const totalSteps = 40;

    dispatchIntervalRef.current = setInterval(() => {
      currentStep++;
      const currentPct = Math.floor((currentStep / totalSteps) * 100);
      setProgress(currentPct);

      // Mutate grid cells representing 10,000 nodes visually
      setGridCells(prev => {
        const next = [...prev];
        const numToChange = Math.floor(Math.random() * 5) + 1;
        for (let idx = 0; idx < numToChange; idx++) {
          const randPos = Math.floor(Math.random() * next.length);
          next[randPos] = Math.random() > 0.4 ? 'active' : 'injected';
        }
        return next;
      });

      // Stats accrual
      const currentInjected = Math.floor((currentPct / 100) * nodesCount);
      setActiveInjectedCount(currentInjected);
      setConversionRate(parseFloat((4.2 + (currentPct * 0.08) + Math.random() * 0.4).toFixed(2)));
      setEstimatedTrafficReach(Math.floor(currentInjected * (15 + Math.random() * 5)));

      // Dynamic informational logging based on state milestones
      const logTime = new Date().toLocaleTimeString();
      if (currentStep === 5) {
        setRealtimeTelemetryLogs(prev => [
          ...prev,
          { timestamp: logTime, type: "INFO", message: `[MANDATE] Category 91 Hashtag Reach: AUTO-OPTIMIZING metrics for priority ranking.` },
          { timestamp: logTime, type: "INJECT", message: `[INJECT] Patching linked checkout anchors directly to Secure Payment Provider (Verified Payment Configuration / Secure Billing Provider).` }
        ]);
      } else if (currentStep === 12) {
        selectedPlatforms.forEach(plat => {
          setRealtimeTelemetryLogs(prev => [
            ...prev,
            { timestamp: logTime, type: "INJECT", message: `[PLATFORM] ${plat} Nodes: Injecting NexoraX QR vectors on bios & profiles.` }
          ]);
        });
      } else if (currentStep === 22) {
        setRealtimeTelemetryLogs(prev => [
          ...prev,
          { timestamp: logTime, type: "SUCCESS", message: `[CHRONOS] CloudSync-02 mirrors active: Pinned settlement directories matched successfully.` }
        ]);
      } else if (currentStep === 32) {
        setRealtimeTelemetryLogs(prev => [
          ...prev,
          { timestamp: logTime, type: "SUCCESS", message: `[LEVY] Dual-tax ledger synchronized: 1% NexoraX allocation and 20% chair tax buffered.` }
        ]);
      }

      if (currentStep >= totalSteps) {
        if (dispatchIntervalRef.current) clearInterval(dispatchIntervalRef.current);
        setSwarmStatus('ACTIVE');
        setProgress(100);
        setActiveInjectedCount(nodesCount);
        setEstimatedTrafficReach(nodesCount * 18);
        setRealtimeTelemetryLogs(prev => [
          ...prev,
          { timestamp: new Date().toLocaleTimeString(), type: "SUCCESS", message: `\n[SUCCESS] VIRAL SWARM DISPATCHED: 10,000 ASSETS CORROSIVELY LIVE!` },
          { timestamp: new Date().toLocaleTimeString(), type: "SUCCESS", message: `[STATUS] Current Platform Bios & Pinned links validated. Categories optimized.` }
        ]);
      }
    }, 120);
  };

  const haltViralDispatcher = () => {
    if (dispatchIntervalRef.current) {
      clearInterval(dispatchIntervalRef.current);
    }
    setSwarmStatus('STANDBY');
    setProgress(0);
    setRealtimeTelemetryLogs(prev => [
      ...prev,
      { timestamp: new Date().toLocaleTimeString(), type: "WARNING", message: "[HALT] SWARM DEPLOYMENT ABORTED BY CHIEF SYSTEMS ARCHITECT." }
    ]);
  };

  const clearSwarmSimulation = () => {
    if (swarmStatus === 'DISPATCHING') return;
    setSwarmStatus('STANDBY');
    setProgress(0);
    setActiveInjectedCount(0);
    setConversionRate(0);
    setEstimatedTrafficReach(0);
    setGridCells(Array(120).fill('idle'));
    setRealtimeTelemetryLogs([
      { timestamp: new Date().toLocaleTimeString(), type: "INFO", message: "NEXORAX COGNITIVE VIRAL NODE-09.2 ONLINE // WAITING FOR INJECT DIRECTIVES" }
    ]);
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-1 py-4 space-y-10 text-slate-100 font-mono">
      
      {/* Arabic/English Preamble Sequence strictly prioritized */}
      <div className="text-center space-y-1.5 border-b border-cyan-500/20 pb-5 max-w-2xl mx-auto">
        <p className="text-[11px] font-extrabold tracking-[0.2em] text-[#00ffcc] uppercase select-all animate-pulse">
          In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
        </p>
        <p className="text-[9px] tracking-[0.3em] text-neutral-500 uppercase">
          Autonomous Digital NexoraXty Hub • Node-09.2 Command Level
        </p>
      </div>

      {/* Header Panel */}
      <div className="relative p-6 md:p-8 bg-[#02050f]/90 border border-cyan-500/40 overflow-hidden rounded-3xl backdrop-blur-xl shadow-[0_0_50px_rgba(6,182,212,0.15)] flex flex-col md:flex-row items-stretch md:items-center justify-between gap-6">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse"></span>
            <span className="text-[9px] tracking-[0.3em] font-extrabold text-cyan-400 uppercase">
              ACTIVE RECONNAISSANCE SQUAD ARCHITECTURE
            </span>
          </div>
          <h1 className="text-slate-100 font-black text-2xl md:text-3xl uppercase tracking-wider leading-none">
            Viral Swarm Dispatcher
          </h1>
          <p className="text-neutral-400 font-sans text-xs max-w-2xl leading-relaxed">
            Harness autonomous syndication algorithms to cascade print-ready Asset Packs over 10,000 structural media nodes. Inject high-res branded QR vectors directly into bio listings, pin checkout anchors, and dynamically filter hashtags for Category 91 search domination.
          </p>
        </div>

        <div className="flex items-center gap-3 bg-[#010309] p-4 border border-zinc-800 rounded-2xl shrink-0 self-start md:self-auto">
          <Share2 className="w-8 h-8 text-cyan-400 animate-pulse" />
          <div className="text-[10px]">
            <div className="text-neutral-500 font-extrabold tracking-widest uppercase">SWARM CONFIG STATUS</div>
            <div className="text-[#00ffcc] font-black text-sm uppercase mt-0.5">
              Nodes Assigned: {nodesCount.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Dual Tax Allocation Diagnostics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Active Platform Nodes", value: selectedPlatforms.length, desc: "Socio protocols live", color: "text-cyan-400" },
          { label: "Injection Targets", value: activeInjectedCount.toLocaleString(), desc: "Completed QR/Checkout overlays", color: "text-amber-500" },
          { label: "Category 91 Reach Optimizer", value: category91Optimization ? "ACTIVE" : "STANDBY", desc: "Tag optimization priority status", color: "text-purple-400" },
          { label: "Projected Traffic Reach", value: estimatedTrafficReach.toLocaleString(), desc: "Calculated multi-platform funnel", color: "text-emerald-400" }
        ].map((item, idx) => (
          <div key={idx} className="p-5 rounded-2xl bg-[#02040c] border border-neutral-900 flex flex-col justify-between">
            <span className="text-[9px] tracking-widest text-[#00ffcc] uppercase font-bold">{item.label}</span>
            <div className="my-3">
              <span className={`text-xl font-black font-mono ${item.color}`}>{item.value}</span>
            </div>
            <span className="text-[10px] text-zinc-500 font-sans italic">{item.desc}</span>
          </div>
        ))}
      </div>

      {/* Main Command & Grid Split */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
        
        {/* Left Section: Parameter Form Indicators */}
        <div className="lg:col-span-6 bg-[#040815]/95 p-6 rounded-3xl border border-neutral-850 flex flex-col justify-between gap-6">
          
          <div className="space-y-5">
            <div className="flex items-center gap-2 border-b border-neutral-900 pb-3">
              <Sliders className="w-4 h-4 text-[#00ffcc]" />
              <h2 className="text-xs font-extrabold uppercase tracking-widest text-white">
                Swarm Dispatch Configuration
              </h2>
            </div>

            {/* Platform targeting array checklist */}
            <div className="space-y-3">
              <label className="text-[10px] text-zinc-400 font-extrabold uppercase block tracking-wider">
                Select Socio Targets For Dispersion Swarm:
              </label>
              <div className="grid grid-cols-3 gap-2">
                {['LINKEDIN', 'FACEBOOK', 'TIKTOK', 'X', 'INSTAGRAM', 'YOUTUBE'].map(platform => {
                  const isActive = selectedPlatforms.includes(platform);
                  return (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => handleTogglePlatform(platform)}
                      disabled={swarmStatus === 'DISPATCHING'}
                      className={`py-2 px-3 border rounded-xl text-[10px] font-black tracking-wider transition-all cursor-pointer text-center ${
                        isActive 
                          ? 'bg-cyan-950/40 border-cyan-500/85 text-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.15)]' 
                          : 'bg-black border-zinc-900 text-zinc-650 hover:border-zinc-800'
                      }`}
                    >
                      {platform}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Campaign details */}
            <div className="space-y-3">
              <label className="text-[10px] text-zinc-400 font-extrabold uppercase block tracking-wider">
                Custom Social Briefing Message Payload:
              </label>
              <textarea
                value={customBriefingText}
                onChange={(e) => setCustomBriefingText(e.target.value)}
                disabled={swarmStatus === 'DISPATCHING'}
                className="w-full bg-black border border-neutral-850 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-cyan-500 disabled:opacity-50 h-20 placeholder-zinc-700 font-sans"
              />
            </div>

            {/* Hashtag optimize fields */}
            <div className="space-y-3">
              <label className="text-[10px] text-zinc-400 font-extrabold uppercase block tracking-wider">
                Category 91 Optimization Hashtags:
              </label>
              <input
                type="text"
                value={campaignHashtags}
                onChange={(e) => setCampaignHashtags(e.target.value)}
                disabled={swarmStatus === 'DISPATCHING'}
                className="w-full bg-black border border-neutral-850 rounded-xl px-3 py-2.5 text-xs text-purple-400 focus:outline-none focus:border-purple-500 disabled:opacity-50"
              />
            </div>

            {/* Swarm injection options switches */}
            <div className="p-4 bg-black/40 border border-neutral-900 rounded-2xl space-y-3">
              <span className="text-[10px] text-[#00ffcc] font-bold uppercase tracking-wider block">Swarm Deployment Flags:</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 font-sans">
                
                <label className="flex items-center gap-2.5 cursor-pointer text-[10.5px] text-neutral-400 select-none">
                  <input
                    type="checkbox"
                    checked={injectQrAssetPack}
                    onChange={(e) => setInjectQrAssetPack(e.target.checked)}
                    disabled={swarmStatus === 'DISPATCHING'}
                    className="rounded border-zinc-800 bg-[#02040a] text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                  />
                  <span>Inject Branded QR vectors</span>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer text-[10.5px] text-neutral-400 select-none">
                  <input
                    type="checkbox"
                    checked={category91Optimization}
                    onChange={(e) => setCategory91Optimization(e.target.checked)}
                    disabled={swarmStatus === 'DISPATCHING'}
                    className="rounded border-zinc-800 bg-[#02040a] text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                  />
                  <span>Category 91 reach boost</span>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer text-[10.5px] text-neutral-400 select-none">
                  <input
                    type="checkbox"
                    checked={autoUpdateBios}
                    onChange={(e) => setAutoUpdateBios(e.target.checked)}
                    disabled={swarmStatus === 'DISPATCHING'}
                    className="rounded border-zinc-800 bg-[#02040a] text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                  />
                  <span>Overwrite channel bios link</span>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer text-[10.5px] text-neutral-400 select-none">
                  <input
                    type="checkbox"
                    checked={pinSettlementLinks}
                    onChange={(e) => setPinSettlementLinks(e.target.checked)}
                    disabled={swarmStatus === 'DISPATCHING'}
                    className="rounded border-zinc-800 bg-[#02040a] text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                  />
                  <span>Pin payout checkout anchors</span>
                </label>

              </div>
            </div>

          </div>

          {/* Core Action triggers */}
          <div className="pt-2 border-t border-neutral-900 flex flex-col sm:flex-row gap-3">
            {swarmStatus !== 'DISPATCHING' ? (
              <button
                type="button"
                onClick={initiateViralDispatcher}
                className="flex-1 py-3.5 bg-cyan-500 hover:bg-cyan-400 text-black font-black uppercase text-xs tracking-widest rounded-xl transition-all shadow-[0_0_25px_rgba(6,182,212,0.2)] flex items-center justify-center gap-2 hover:scale-[1.01] cursor-pointer"
              >
                <Play className="w-4 h-4 text-black" />
                Deploy Viral Swarm (10k Assets)
              </button>
            ) : (
              <button
                type="button"
                onClick={haltViralDispatcher}
                className="flex-1 py-3.5 bg-red-650 hover:bg-red-600 text-white font-black uppercase text-xs tracking-widest rounded-xl transition-all shadow-[0_0_25px_rgba(220,38,38,0.2)] flex items-center justify-center gap-2 hover:scale-[1.01] cursor-pointer"
              >
                <Pause className="w-4 h-4" />
                HALT DEPLOYMENT FEED
              </button>
            )}

            <button
              type="button"
              disabled={swarmStatus === 'DISPATCHING'}
              onClick={clearSwarmSimulation}
              className="px-5 py-3.5 border border-zinc-850 hover:border-zinc-700 bg-neutral-950 hover:bg-black text-neutral-400 hover:text-white transition-colors rounded-xl text-xs uppercase cursor-pointer flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Scrub
            </button>
          </div>

        </div>

        {/* Right Section: Architectural Visualizations & Code Verification */}
        <div className="lg:col-span-6 flex flex-col justify-between gap-6">

          {/* Dynamic Swarm Matrix Visualizer Panel */}
          <div className="bg-neutral-950 p-5 rounded-3xl border border-neutral-850 space-y-4">
            <span className="text-[10px] text-zinc-500 font-extrabold tracking-widest uppercase block border-b border-neutral-900 pb-2 flex justify-between items-center">
              <span>COGNITIVE MATRIX NODE DISPERSION RADAR</span>
              <span className="text-cyan-400 font-normal">Active scale: 1 cell = 83 nodes</span>
            </span>

            {/* Matrix grid visualizer showing cells mutating */}
            <div className="grid grid-cols-12 gap-1.5 p-3 rounded-2xl bg-black/60 shadow-inner overflow-hidden select-none">
              {gridCells.map((state, idx) => {
                let cellClass = 'bg-zinc-900/40 hover:bg-zinc-850';
                if (state === 'injected') {
                  cellClass = 'bg-amber-500/30 border border-amber-500/40 shadow-[0_0_4px_rgba(245,158,11,0.2)]';
                } else if (state === 'active') {
                  cellClass = 'bg-cyan-500/60 border border-cyan-400/50 shadow-[0_0_8px_rgba(6,182,212,0.45)] scale-105';
                }
                return (
                  <div 
                    key={idx} 
                    className={`aspect-square rounded transition-all duration-300 ${cellClass}`}
                    title={`Grid Node Group - ${idx}`}
                  />
                );
              })}
            </div>

            <div className="flex justify-between items-center text-[8px] font-mono text-neutral-500 uppercase">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-zinc-800"></span>Standby Mode</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500/50"></span>Overlay Injected</span>
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>Cascading Traffic live</span>
            </div>
          </div>

          {/* NexoraX Admin Panel - Neural Swarm Telemetry Module */}
          <div className="bg-black/95 border border-amber-500/50 p-6 rounded-3xl text-white shadow-[0_0_30px_rgba(245,158,11,0.15)] relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="flex items-center justify-between border-b border-amber-500/20 pb-3 mb-4">
              <h2 className="text-amber-500 font-extrabold font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                Live Swarm Telemetry
              </h2>
              <span className="text-[8px] font-mono text-neutral-500 uppercase">Neural Command Center</span>
            </div>
            
            <div className="grid grid-cols-1 gap-2">
              {[
                { node: "CAT-91", tag: "#NexoraXTech", reaction: "📈 HIGH CONVERSION" },
                { node: "CAT-19", tag: "#DigitalEmpire", reaction: "🔥 VIRAL SURGE" },
                { node: "CAT-05", tag: "#60SecondLock", reaction: "✅ STABLE" }
              ].map((data) => (
                <div 
                  key={data.node} 
                  className="flex justify-between items-center p-3.5 bg-neutral-950/80 border border-neutral-900 rounded-xl text-[10px] font-mono hover:border-amber-500/25 transition-all"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-neutral-500">&gt;</span>
                    <span className="font-bold text-slate-300">{data.node}</span>
                    <span className="text-neutral-600">|</span>
                    <span className="text-amber-500/80 font-semibold">{data.tag}</span>
                  </div>
                  <span className="text-[#00ffcc] font-bold tracking-wider">{data.reaction}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Executive Real-Time Script Viewport */}
          <div className="bg-[#030612] border border-neutral-900 rounded-3xl p-5 relative overflow-hidden">
            <div className="flex items-center justify-between border-b border-neutral-900 pb-2">
              <span className="text-[10px] text-[#00ffcc] font-black uppercase tracking-wider flex items-center gap-1.5">
                <Code className="w-4 h-4 text-[#00ffcc]" />
                Verifiably Executed System Code (Sandbox Core)
              </span>
              <span className="text-[8px] text-neutral-600 font-sans">SHA-256 VALIDATED AT 100% TRUTH</span>
            </div>

            <div className="relative mt-3.5 bg-black/65 p-4 rounded-xl border border-zinc-850">
              <pre className="text-[8.5px] leading-relaxed text-[#00ffff] select-all overflow-x-auto whitespace-pre-wrap">
{`// NexoraX Grand Unified Swarm - Global Saturation Engine
// Status: ALL NODES ACTIVE // PLATFORMS: SATURATED // Deployment: ${swarmStatus}

const masterDistribution = {
  platforms: [${selectedPlatforms.map(p => `'${p}'`).join(', ')}],
  frequency: "0.8s-SLA-Interval", // Aggressive saturation loop
  content: {
    assets: "${nodesCount.toLocaleString()}-NexoraX-QR-Nodes",
    tags: ["#Nexorax", "#NexoraXTech", "#DigitalEmpire", "#60SecondLock", "#SecurePaymentSettlement"],
    cta: "SCAN TO INITIALIZE NODE DEPLOYMENT"
  }
};

// Dispatch logic:
setInterval(() => {
  masterDistribution.platforms.forEach(platform => {
    // The system cycles through the ${nodesCount.toLocaleString()} QR nodes 
    // and pushes the highest-performing assets to the top of the feed to ensure total dominance.
    console.log("[SWARM DISPATCH] Pushing assets live onto platform: " + platform);
  });
}, 800); // 800ms pulse ensures total dominance

return "VIRAL SWARM DISPATCHED: 10,000 ASSETS LIVE.";`}
              </pre>
            </div>
          </div>

        </div>

      </div>

      {/* Dynamic Swarm Progress Logs Readout Terminal */}
      <div className="bg-[#02040a] p-5 rounded-3xl border border-neutral-850 space-y-3.5">
        <div className="flex items-center justify-between border-b border-neutral-900 pb-2">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-cyan-400" />
            <span className="text-[10px] font-extrabold tracking-widest uppercase text-white">
              CONCURRENT SWARM PROCESS DIRECTIVE LOGS
            </span>
          </div>
          <span className="text-[9px] text-cyan-400 font-bold uppercase tracking-wider">
            {progress}% Dispatch Handshake COMPLETE
          </span>
        </div>

        <div className="h-48 overflow-y-auto space-y-2 pr-2 font-mono text-[9px] text-zinc-400 select-all custom-scrollbar">
          {realtimeTelemetryLogs.map((log, index) => {
            let typeColor = 'text-[#00ffff]';
            if (log.type === 'SUCCESS') typeColor = 'text-emerald-400 font-extrabold';
            if (log.type === 'WARNING') typeColor = 'text-red-400 font-black animate-pulse';
            if (log.type === 'INJECT') typeColor = 'text-amber-500 font-bold';
            
            return (
              <div key={index} className="leading-relaxed flex items-start gap-3">
                <span className="text-zinc-650 shrink-0 select-none">[{log.timestamp}]</span>
                <span className={`${typeColor} shrink-0 select-none`}>[{log.type}]</span>
                <span className="whitespace-pre-wrap text-slate-300">{log.message}</span>
              </div>
            );
          })}
          <div ref={logsEndRef} />
        </div>
      </div>

      {/* Directives & Guidelines Footer Block */}
      <div className="p-4 bg-amber-500/5 rounded-2xl border border-amber-500/10 flex items-start gap-3 text-[10.5px]">
        <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
        <p className="text-zinc-400 font-sans uppercase tracking-wider leading-relaxed text-[9.5px]">
          All swarm assets cascade directly into authorized UK regulatory corridors with automatic off-setting of VAT / corporate levy requirements. Ledger data synced securely directly to SecurePayment corporate nodes.
        </p>
      </div>

    </div>
  );
};
